import { createContext, useContext, useState, useRef } from 'react';
import { Form, Modal, Select, Input, InputNumber, Switch, Button } from 'antd';

import { useCarMentenanceContext } from './carMaintenanceProvider';

const EditCarCatalogsModalContext = createContext();

const initialFormValues = {
  id: null,
  img_path: '',
  car_type: '',
  name: '',
  height: 0,
  width: 0,
  length: 0,
  price: 0,
  price_until: 0,
  show_room_url: '',
  is_cancel: false,
};

export const EditCarCatalogsModalProvider = ({ children }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [carEditForm] = Form.useForm();
  const editAction = useRef(false);

  const { createCarCatalog } = useCarMentenanceContext();

  const showModal = (carData = null) => {
    editAction.current = !!carData;
    // 如果有傳入 carData，使用傳入的資料；否則使用 initialFormValues
    const dataToUse = carData || initialFormValues;

    if (dataToUse.id !== null && dataToUse.id !== undefined) {
      carEditForm.setFieldsValue({
        id: dataToUse.id,
        img_path: dataToUse.img_path,
        car_type: dataToUse.car_type,
        name: dataToUse.name,
        height: dataToUse.height || 0,
        width: dataToUse.width || 0,
        length: dataToUse.length || 0,
        price: dataToUse.price || 0,
        price_until: dataToUse.price_until || 0,
        show_room_url: dataToUse.show_room_url,
        is_active: !dataToUse.is_cancel,
      });
    } else {
      carEditForm.resetFields();
      carEditForm.setFieldsValue({
        is_active: true,
      });
    }
    setIsModalVisible(true);
  };

  const handleModalReset = () => {
    carEditForm.resetFields();
    setIsModalVisible(false);
  };

  const handleModalOnFinish = async () => {
    const submitData = carEditForm.getFieldsValue();
    const convertedData = {
      ...submitData,
      img_path: btoa(submitData.img_path),
      show_room_url: btoa(submitData.show_room_url),
      is_cancel: !submitData.is_active,
    };

    console.log('submitData:', convertedData);
    await createCarCatalog(convertedData, submitData.id);
    handleModalReset();
  };

  return (
    <EditCarCatalogsModalContext.Provider
      value={{
        showModal,
        isModalVisible,
        handleModalReset,
        carEditForm,
        handleModalOnFinish,
        editAction,
      }}>
      {children}
    </EditCarCatalogsModalContext.Provider>
  );
};

export const EditCarCatalogsModal = () => {
  const {
    isModalVisible,
    handleModalReset,
    carEditForm,
    handleModalOnFinish,
    editAction,
  } = useEditModal();

  const { carTypes, isLoading } = useCarMentenanceContext();

  const formatter = (value) => {
    const [start, end] = `${value}`.split('.') || [];
    const v = `${start}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `${end ? `${v}.${end}` : `${v}`}`;
  };

  return (
    <Modal
      title={editAction.current ? '編輯 車款' : '新增 車款'}
      open={isModalVisible}
      onCancel={handleModalReset}
      footer={null}>
      <Form
        form={carEditForm}
        name="carEditForm"
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 16 }}
        layout="horizontal"
        onFinish={handleModalOnFinish}
        onCancel={handleModalReset}
        style={{ maxWidth: 600 }}>
        <Form.Item label="ID" name="id" hidden>
          <Input />
        </Form.Item>
        <Form.Item
          label="照片URL"
          name="img_path"
          rules={[
            { required: true, message: '請輸入車款照片URL' },
            { type: 'url', message: '請輸入合法的網址格式' },
          ]}>
          <Input allowClear />
        </Form.Item>
        <Form.Item
          label="車型"
          name="car_type"
          rules={[{ required: true, message: '請選擇車型' }]}>
          <Select>
            {carTypes.map((carType) => (
              <Select.Option key={carType.id} value={carType.name}>
                {`${carType.name} - ${carType.description}`}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label="車款名稱"
          name="name"
          rules={[{ required: true, message: '請輸入車款名稱' }]}>
          <Input allowClear />
        </Form.Item>
        <Form.Item
          label="高度"
          name="height"
          rules={[{ required: true, message: '請輸入車款高度' }]}>
          <InputNumber min={0} step={1} addonAfter="mm" formatter={formatter} />
        </Form.Item>
        <Form.Item
          label="寬度"
          name="width"
          rules={[{ required: true, message: '請輸入車款寬度' }]}>
          <InputNumber min={0} step={1} addonAfter="mm" formatter={formatter} />
        </Form.Item>
        <Form.Item
          label="長度"
          name="length"
          rules={[{ required: true, message: '請輸入車款長度' }]}>
          <InputNumber min={0} step={1} addonAfter="mm" formatter={formatter} />
        </Form.Item>
        <Form.Item
          label="價格起"
          name="price"
          rules={[
            { required: true, message: '請輸入車款價格下限' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                const priceUntil = getFieldValue('price_until');
                if (!value || !priceUntil || value <= priceUntil) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('價格起必須小於或等於價格止'));
              },
            }),
          ]}>
          <InputNumber
            min={0}
            step={0.1}
            precision={1}
            addonAfter="萬"
            formatter={formatter}
          />
        </Form.Item>
        <Form.Item
          label="價格止"
          name="price_until"
          rules={[
            { required: true, message: '請輸入車款價格上限' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                const price = getFieldValue('price');
                if (!value || !price || value >= price) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('價格止必須大於或等於價格起'));
              },
            }),
          ]}>
          <InputNumber
            min={0}
            step={0.1}
            precision={1}
            addonAfter="萬"
            formatter={formatter}
          />
        </Form.Item>
        <Form.Item
          label="展示間網址"
          name="show_room_url"
          rules={[
            { required: true, message: '請輸入車款展示間網址' },
            { type: 'url', message: '請輸入合法的網址格式' },
          ]}>
          <Input allowClear />
        </Form.Item>
        <Form.Item label="啟用狀態" name="is_active">
          <Switch />
        </Form.Item>
        <div style={{ textAlign: 'right', marginTop: '24px' }}>
          <Button onClick={handleModalReset} style={{ marginRight: '8px' }}>
            關閉
          </Button>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            提交
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default EditCarCatalogsModalProvider;

export const useEditModal = () => {
  return useContext(EditCarCatalogsModalContext);
};
